import type { ReactRouteComponent, ReactRouteDefinition, ReactRouteSnapshot } from './types';
import type { ReadonlySignal } from '@preact/signals-core';
import type { AppLocation } from 'src/lib/navigation/types';
import { RouteCore } from 'src/lib/router/core';
import type { ReactContainer } from 'src/ReactContainer';
import type { WebNavigationHost } from 'src/services/NavigationService';

export class ReactRouter implements Disposable {
    readonly #core: RouteCore<ReactContainer, ReactRouteComponent>;
    readonly #navigation: WebNavigationHost;
    readonly #transitionRegistration: Disposable;

    get current(): ReadonlySignal<ReactRouteSnapshot | null> {
        return this.#core.current;
    }

    get pendingLocation(): ReadonlySignal<AppLocation | null> {
        return this.#core.pendingLocation;
    }

    get navigation(): WebNavigationHost {
        return this.#navigation;
    }

    constructor(routes: readonly ReactRouteDefinition[], container: ReactContainer, navigation: WebNavigationHost) {
        this.#navigation = navigation;
        this.#core = new RouteCore(routes, container);
        this.#transitionRegistration = navigation.setTransitionHandler(this.#core);
    }

    async start(location: AppLocation): Promise<void> {
        const result = await this.#navigation.navigate(location, { replace: true, state: location.state });
        if (result.status !== 'completed') {
            throw new Error(`Initial route preparation ${result.status}`);
        }
    }

    [Symbol.dispose](): void {
        this.#transitionRegistration[Symbol.dispose]();
        this.#core[Symbol.dispose]();
    }
}
